import { fromNumber, toNumber, type MonetaryValue } from 'ezmoney';

import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { type CostCenter } from './costCenter';
import {
  type CustomField,
  customFieldsToCustomFieldAssociation,
} from './customField';
import { type CustomFieldAssociation } from './customFieldAssociation';
import { type CustomFieldDefinition } from './customFieldDefinition';

// analytical split items fetch payload - uses cost centers details and custom field
export interface AnalyticalSplitItemDetails {
  amount: MonetaryValue;
  percentage: number;
  costCenter?: CostCenter;
  customFields?: CustomField[];
}

// analytical split items post payload - uses cost centers id and custom fields associations
export interface AnalyticalSplitItem {
  amount: MonetaryValue;
  percentage: number;
  costCenterId?: string;
  customFields?: CustomFieldAssociation[];
}

// analytical split items forms values
// detailed form setup (activate split, dimensions...)
// to preserve input data while enabling / disabling split
export type AnalyticalSplitDimensions = {
  splitOnCostCenter?: boolean;
  splitOnCustomFieldsIds?: string[];
};

// item form values are partial and have numbers for amounts
export type AnalyticalSplitItemFormValue = Omit<
  Partial<AnalyticalSplitItem>,
  'amount'
> & {
  id: string; // this id is used for form elements tracking on the frontend only (esp errors). Not sent to the backend
  amount?: number;
};

export interface AnalyticalSplitFormValue {
  activate?: boolean;
  dimensions: AnalyticalSplitDimensions;
  items: AnalyticalSplitItemFormValue[];
}

export const analyticalSplitItemToFormValue = (
  item: AnalyticalSplitItemDetails,
  index: number,
): AnalyticalSplitItemFormValue => ({
  ...item,
  id: `item-${index}`,
  costCenterId: item.costCenter?.id,
  amount: toNumber(item.amount),
  customFields: customFieldsToCustomFieldAssociation(item.customFields ?? []),
});

// initialize form values from analytical split items received from API
export const analyticalSplitItemsToFormValue = (
  items?: AnalyticalSplitItemDetails[],
): AnalyticalSplitFormValue | undefined => {
  if (!items?.length) {
    return undefined;
  }

  const splitOnCustomFieldsIds = [
    ...new Set(
      items.flatMap(
        (item) => item.customFields?.map((cf) => cf.field.id) ?? [],
      ),
    ),
  ];

  return {
    activate: true,
    dimensions: {
      splitOnCostCenter: items.some((item) => !!item.costCenter),
      splitOnCustomFieldsIds,
    },
    items: items.map(analyticalSplitItemToFormValue),
  };
};

const getInitializedBudgetTrackingFields = (
  eligibleCustomFields: CustomFieldDefinition[],
  expenseCategoryCustomFieldId?: string,
  rootValues?: {
    costCenterId?: string;
    customFieldsAssociations?: CustomFieldAssociation[];
  },
): {
  costCenterId?: string;
  customFields?: CustomFieldAssociation[];
} => {
  return {
    costCenterId: rootValues?.costCenterId,
    // note: we can initialize with all eligible root values
    // the display and final submission will be driven by splitOnCustomFieldsIds
    customFields: rootValues?.customFieldsAssociations?.filter(
      (cfAssoc) =>
        cfAssoc.customFieldId === expenseCategoryCustomFieldId ||
        eligibleCustomFields.some((f) => f.id === cfAssoc.customFieldId),
    ),
  };
};

// initialize with 50/50 split on current "root" cost center
export const initializeAnalyticalSplitFormValue = (
  totalAmount: number,
  eligibleCustomFields: CustomFieldDefinition[],
  expenseCategoryCustomFieldId?: string,
  rootValues?: {
    costCenterId?: string;
    customFieldsAssociations?: CustomFieldAssociation[];
  },
): AnalyticalSplitFormValue => {
  const initialBudgetTrackingFields = getInitializedBudgetTrackingFields(
    eligibleCustomFields,
    expenseCategoryCustomFieldId,
    rootValues,
  );

  return {
    activate: true,
    dimensions: {
      // populate initial dimensions values but only activate cost center and expense category
      // so the user will be able to retrieve the "root" values when adding dimensions
      splitOnCostCenter: true,
      splitOnCustomFieldsIds: expenseCategoryCustomFieldId
        ? [expenseCategoryCustomFieldId]
        : [],
    },
    items: [
      {
        id: `item-0`,
        percentage: 50,
        amount: totalAmount / 2,
        ...initialBudgetTrackingFields,
      },
      {
        id: `item-1`,
        percentage: 50,
        amount: totalAmount ? totalAmount / 2 : 0,
        ...initialBudgetTrackingFields,
      },
    ],
  };
};

// create a new item with the same budget tracking fields as the ref one
// and an amount that completes the total (or 0 if already complete)
export const getNewAnalyticalSplitItem = (
  index: number,
  totalAmount: number,
  totalAmountAllocated: number,
  eligibleCustomFields: CustomFieldDefinition[],
  expenseCategoryCustomFieldId?: string,
  refValues?: {
    costCenterId?: string;
    customFieldsAssociations?: CustomFieldAssociation[];
  },
): AnalyticalSplitItemFormValue => {
  // if the split does not yet sum to totalAmount
  // then initialize the item to complete it
  const currentTotalRemaining = totalAmount - totalAmountAllocated;

  const initialBudgetTrackingFields = getInitializedBudgetTrackingFields(
    eligibleCustomFields,
    expenseCategoryCustomFieldId,
    refValues,
  );

  return currentTotalRemaining > 0
    ? {
        id: `item-${index}`,
        percentage: (currentTotalRemaining / totalAmount) * 100,
        amount: currentTotalRemaining,
        ...initialBudgetTrackingFields,
      }
    : {
        id: `item-${index}`,
        percentage: 0,
        amount: 0,
        ...initialBudgetTrackingFields,
      };
};

const analyticalSplitItemFormValueToItem = (
  currency: string,
  item: AnalyticalSplitItemFormValue,
  splitOnCostCenter?: boolean,
  splitOnCustomFieldsIds?: string[],
): AnalyticalSplitItem => {
  const { amount = 0, percentage = 0, costCenterId, customFields } = item;

  return {
    ...item,
    amount: fromNumber(amount, currency, 2),
    percentage,
    costCenterId: splitOnCostCenter ? costCenterId : undefined,
    customFields: customFields?.filter((cfAssoc) =>
      splitOnCustomFieldsIds?.includes(cfAssoc.customFieldId),
    ),
  };
};

export const analyticalSplitFormValueToItems = ({
  currency,
  value,
}: {
  currency: string;
  value?: AnalyticalSplitFormValue;
}): AnalyticalSplitItem[] | undefined => {
  if (!value?.activate) {
    return undefined;
  }

  const {
    dimensions: { splitOnCostCenter, splitOnCustomFieldsIds },
  } = value;

  return value.items.map((item) =>
    analyticalSplitItemFormValueToItem(
      currency,
      item,
      splitOnCostCenter,
      splitOnCustomFieldsIds,
    ),
  );
};

export function emitTrackingEvents(
  step: 'invoiceRequest' | 'invoiceUpdate' | 'invoiceReview',
  requestId: string,
) {
  track(AnalyticEventName.ANALYTICAL_SPLIT_SUBMITTED, {
    step,
    requestId,
  });
}
